import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Student, Academy } from '../types';
import { X } from 'lucide-react';

interface StudentFormProps {
  onClose: () => void;
  onSave: (student: Student) => void;
}

declare global {
  interface Window {
    naver: any;
  }
}

const StudentForm: React.FC<StudentFormProps> = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    birth_year: new Date().getFullYear(),
    school: '',
    pickup_address1: '',
    pickup_address2: '',
    dropoff_address1: '',
    dropoff_address2: '',
    student_phone: '',
    parent_phone: '',
    notes: '',
    academy_distance: 0
  });

  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [activeAddressField, setActiveAddressField] = useState<string | null>(null);
  const [schoolSuggestions, setSchoolSuggestions] = useState<any[]>([]);
  const [firstAcademy, setFirstAcademy] = useState<Academy | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${import.meta.env.VITE_NAVER_CLIENT_ID}&submodules=geocoder`;
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      loadFirstAcademy();
    };

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const loadFirstAcademy = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('academies')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    if (error) {
      console.error('Error loading academy:', error);
      return;
    }

    if (data && window.naver) {
      window.naver.maps.Service.geocode({
        query: data.address
      }, function(status: any, response: any) {
        if (status === window.naver.maps.Service.Status.ERROR) return;

        if (response.v2.meta.totalCount > 0) {
          const item = response.v2.addresses[0];
          setFirstAcademy({
            ...data,
            coordinates: {
              lat: parseFloat(item.y),
              lng: parseFloat(item.x)
            }
          });
        }
      });
    }
  };

  const handleSchoolSearch = async (query: string) => {
    if (!query.trim()) {
      setSchoolSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://map.naver.com/v5/api/search?caller=pcweb&query=${encodeURIComponent(query + ' 학교')}&type=all&page=1&displayCount=20&isPlaceRecommendationReplace=true&lang=ko`
      );
      
      if (!response.ok) {
        setSchoolSuggestions([]);
        return;
      }
      
      const data = await response.json();
      if (data.result?.place?.list) {
        setSchoolSuggestions(
          data.result.place.list.filter((item: any) => 
            item.category.includes('학교') || 
            item.name.includes('학교')
          )
        );
      }
    } catch (error) {
      console.error('School search error:', error);
      setSchoolSuggestions([]);
    }
  };

  const handleSchoolSelect = (place: any) => {
    setFormData(prev => ({
      ...prev,
      school: place.name
    }));
    setSchoolSuggestions([]);
  };

  const handleAddressSearch = (query: string, field: string) => {
    if (!query.trim() || !window.naver) {
      setAddressSuggestions([]);
      return;
    }

    setActiveAddressField(field);
    
    window.naver.maps.Service.geocode({
      query: query
    }, function(status: any, response: any) {
      if (status === window.naver.maps.Service.Status.ERROR) {
        setAddressSuggestions([]);
        return;
      }

      if (response.v2.meta.totalCount > 0) {
        setAddressSuggestions(response.v2.addresses);
      } else {
        setAddressSuggestions([]);
      }
    });
  };

  const handleAddressSelect = async (address: any) => {
    if (!activeAddressField || !firstAcademy?.coordinates) return;

    const selectedLat = parseFloat(address.y);
    const selectedLng = parseFloat(address.x);
    const distance = calculateDistance(
      firstAcademy.coordinates.lat,
      firstAcademy.coordinates.lng,
      selectedLat,
      selectedLng
    );

    setFormData(prev => ({
      ...prev,
      [activeAddressField]: address.roadAddress || address.jibunAddress,
      academy_distance: distance
    }));
    setAddressSuggestions([]);
    setActiveAddressField(null);
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return Math.round(R * c); // Distance in meters
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('로그인이 필요합니다.');
        return;
      }

      const { data, error } = await supabase
        .from('students')
        .insert([
          {
            ...formData,
            user_id: user.id
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error saving student:', error);
        alert('학생 정보 저장 중 오류가 발생했습니다.');
        return;
      }

      if (data) {
        onSave(data);
        onClose();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('학생 정보 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>
        
        <h2 className="text-2xl font-bold mb-6">학생 정보 입력</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">이름</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">출생년도</label>
              <input
                type="number"
                required
                min="1900"
                max={new Date().getFullYear()}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.birth_year}
                onChange={(e) => setFormData(prev => ({ ...prev, birth_year: parseInt(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">학교</label>
            <div className="relative">
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.school}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, school: e.target.value }));
                  handleSchoolSearch(e.target.value);
                }}
              />
              {schoolSuggestions.length > 0 && (
                <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                  {schoolSuggestions.map((place, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSchoolSelect(place)}
                    >
                      {place.name}
                      <div className="text-sm text-gray-500">{place.address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">등원 주소 1</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.pickup_address1}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, pickup_address1: e.target.value }));
                    handleAddressSearch(e.target.value, 'pickup_address1');
                  }}
                />
                {activeAddressField === 'pickup_address1' && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleAddressSelect(suggestion)}
                      >
                        {suggestion.roadAddress || suggestion.jibunAddress}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">등원 주소 2</label>
              <div className="relative">
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.pickup_address2}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, pickup_address2: e.target.value }));
                    handleAddressSearch(e.target.value, 'pickup_address2');
                  }}
                />
                {activeAddressField === 'pickup_address2' && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleAddressSelect(suggestion)}
                      >
                        {suggestion.roadAddress || suggestion.jibunAddress}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">하원 주소 1</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.dropoff_address1}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, dropoff_address1: e.target.value }));
                    handleAddressSearch(e.target.value, 'dropoff_address1');
                  }}
                />
                {activeAddressField === 'dropoff_address1' && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleAddressSelect(suggestion)}
                      >
                        {suggestion.roadAddress || suggestion.jibunAddress}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">하원 주소 2</label>
              <div className="relative">
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.dropoff_address2}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, dropoff_address2: e.target.value }));
                    handleAddressSearch(e.target.value, 'dropoff_address2');
                  }}
                />
                {activeAddressField === 'dropoff_address2' && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleAddressSelect(suggestion)}
                      >
                        {suggestion.roadAddress || suggestion.jibunAddress}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">학생 연락처</label>
              <input
                type="tel"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.student_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, student_phone: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">부모님 연락처</label>
              <input
                type="tel"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.parent_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, parent_phone: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">메모</label>
            <textarea
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              저장
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentForm;